/**
 * src/gm/session/session-store.ts — 会话持久化（设计稿 §2.2/§2.4）。
 *
 * 键：tp3:session:<projectId>:<stage>。容量纪律：会话是 localStorage 里最大的键，
 * 随阶段关闭保留（供排查/再派一轮复用），随项目结算/放弃整体清除——
 * 会话是工作记忆，不是档案（留档产出在 artifacts，留档过程在事件流）。
 */

import { sessionKey, sessionPrefixOf } from '../platform/config'
import { GmError, isQuotaExceeded } from '../platform/errors'
import { listKeys, removeKey, safeStorage } from '../platform/storage'
import { decodeSession, encodeSession, type SessionRecord, type StageKind } from './session-codec'

export function loadSession(projectId: string, stage: StageKind): SessionRecord | null {
  const s = safeStorage()
  if (!s) return null
  let raw: string | null = null
  try {
    raw = s.getItem(sessionKey(projectId, stage))
  } catch {
    return null
  }
  return decodeSession(raw)
}

/** 落盘会话（写前读由调用方保证；配额超限映射 STORAGE_FULL） */
export function saveSession(rec: SessionRecord): void {
  const s = safeStorage()
  if (!s) return
  const next: SessionRecord = { ...rec, updatedAt: new Date().toISOString() }
  try {
    s.setItem(sessionKey(rec.projectId, rec.stage), encodeSession(next))
  } catch (err) {
    if (isQuotaExceeded(err)) {
      throw new GmError(
        'STORAGE_FULL',
        '存档写不进去了（浏览器存储已满）——先去「电脑 → 系统设置」导出存档备份，再清理图鉴老作品或重置存档。',
        err,
      )
    }
    throw err
  }
  // updatedAt 回写到调用方对象，保证后续 save 链上的时间戳一致
  rec.updatedAt = next.updatedAt
}

/** 项目结算/放弃时清除全部会话（设计稿 §2.2.5） */
export function clearProjectSessions(projectId: string): string[] {
  const keys = listKeys(sessionPrefixOf(projectId))
  for (const k of keys) removeKey(k)
  return keys
}

/** 删除单个阶段会话（事故三选"再派一轮"：旧尝试归档删除，新一轮重开） */
export function clearSession(projectId: string, stage: StageKind): void {
  removeKey(sessionKey(projectId, stage))
}

export function listProjectSessionKeys(projectId: string): string[] {
  return listKeys(sessionPrefixOf(projectId))
}
